import { Box, Button, CloseButton, Flex, Group, Loader, Progress, Stack, Text, TextInput } from "@mantine/core";
import { IChecklistItemRow } from "@mctypes/checklist.types";
import { RowClassParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { STANDARD_COLORS } from "../../../globalConstants";
import { ChecklistSummaryTable } from "../ChecklistSummaryTable";
import {
  selectActiveChecklistCheckKeysForPage,
  selectActiveChecklist,
  selectChecklistCategoryCollapseStateForPage,
  selectChecklistDisplayRowsForPage,
  selectChecklistHiddenColumns,
  selectChecklistItems,
  selectChecklistProgressSegmentsForPage,
  selectRefactoredCollapsedCategoryIds,
  selectRefactoredExpandedCategoryIds,
} from "../checklistSelectors";
import {
  thunkToggleAllRefactoredCategoriesCollapse,
  thunkToggleChecklistInactive,
  thunkToggleRefactoredCategoryCollapse,
  thunkUpdateChecklistCategory,
  thunkUpdateChecklistItem,
} from "../checklistThunk";
import { DEFAULT_CHECKLIST_COL_DEF, getChecklistColumns } from "./checklistColumns";
import "./ChecklistTableRefactored.css";

const ChecklistBoardAgGridRefactored: React.FC = () => {
  const dispatch = useAppDispatch();
  const { checklistId } = useParams<{ checklistId: string }>();
  const checklist = useAppSelector((state) => selectActiveChecklist(state, checklistId));
  const checklistItems = useAppSelector((state) => selectChecklistItems(state, checklistId));
  const rows = useAppSelector((state) => selectChecklistDisplayRowsForPage(state, checklistId));
  const activeCheckKeys = useAppSelector((state) => selectActiveChecklistCheckKeysForPage(state, checklistId));
  const progressSegments = useAppSelector((state) => selectChecklistProgressSegmentsForPage(state, checklistId));
  const hiddenColumns = useAppSelector(selectChecklistHiddenColumns);
  const collapseState = useAppSelector((state) => selectChecklistCategoryCollapseStateForPage(state, checklistId));
  const refactoredCollapsedCategoryIds = useAppSelector(selectRefactoredCollapsedCategoryIds);
  const refactoredExpandedCategoryIds = useAppSelector(selectRefactoredExpandedCategoryIds);
  const [filterText, setFilterText] = useState("");
  const [showStats, setShowStats] = useState(false);

  const columns =
    checklist == null
      ? []
      : getChecklistColumns(checklist, activeCheckKeys, hiddenColumns, {
          allCategoriesCollapsed: collapseState.allCategoriesCollapsed,
          collapsedCategoryIds: collapseState.collapsedCategoryIds,
          onToggleAllCategories: () =>
            dispatch(
              thunkToggleAllRefactoredCategoriesCollapse(
                collapseState,
                refactoredCollapsedCategoryIds,
                refactoredExpandedCategoryIds,
              ),
            ),
          onToggleCategory: (categoryId) =>
            dispatch(
              thunkToggleRefactoredCategoryCollapse(
                categoryId,
                collapseState,
                refactoredCollapsedCategoryIds,
                refactoredExpandedCategoryIds,
              ),
            ),
          onToggleInactive: (row) => dispatch(thunkToggleChecklistInactive(checklistId, row)),
          onUpdateCategory: (categoryId, attribute, oldValue, newValue) =>
            dispatch(thunkUpdateChecklistCategory(checklistId, categoryId, attribute, oldValue, newValue)),
          onUpdateItem: (itemId, path, oldValue, newValue) =>
            dispatch(thunkUpdateChecklistItem(checklistId, itemId, path, oldValue, newValue)),
        });

  if (checklist == null) {
    return (
      <Group justify="center" flex={1} p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="xs" h="100%" mih={0}>
      <div className="d-flex m-2">
        <h3 className="d-flex align-items-center">
          <div className="mx-2">{checklist.name}</div>
        </h3>
        <Flex justify="flex-end" align="center" style={{ flexGrow: 1 }}>
          <div className="w-100" style={{ maxWidth: "40%", minWidth: 260 }}>
            <Text size="xs" c="dimmed" ta="right" mb={3}>
              {rows.length} rader
            </Text>
            <Progress.Root className="w-100" h={16} radius={4}>
              <Progress.Section value={progressSegments.accepted} color={STANDARD_COLORS.green} />
              <Progress.Section value={progressSegments.rejected} color={STANDARD_COLORS.red} />
            </Progress.Root>
          </div>
        </Flex>
      </div>

      <Flex align="center" gap="xs" wrap="wrap" className="mx-2 mb-2">
        <TextInput
          size="sm"
          placeholder="Søk..."
          value={filterText}
          onChange={(event) => setFilterText(event.currentTarget.value)}
          w={170}
          rightSection={filterText.length > 0 && <CloseButton onClick={() => setFilterText("")} size="sm" />}
        />

        <Group gap="xs" wrap="nowrap" style={{ marginLeft: "auto" }}>
          <Button color="lightgray.9" size="sm-compact" variant="outline" onClick={() => setShowStats(!showStats)}>
            <i className="fa fa-bar-chart fa-fw" />
            Vis statistikk
          </Button>
        </Group>
      </Flex>

      {showStats && (
        <div className="bg-light p-3">
          <ChecklistSummaryTable checklist={checklist} items={checklistItems} />
        </div>
      )}

      <div className="d-flex flex-column border h-100 overflow-auto">
        <Box className="checklistTable ag-theme-quartz h-100" w="100%">
          <AgGridReact<IChecklistItemRow>
            rowData={rows}
            columnDefs={columns}
            defaultColDef={DEFAULT_CHECKLIST_COL_DEF}
            getRowId={(params) => params.data._id}
            getRowClass={(params: RowClassParams<IChecklistItemRow>) =>
              params.data?.originalItem == null ? "checklist-refactored-category-row" : undefined
            }
            localeText={AG_GRID_LOCALE_NOR}
            quickFilterText={filterText}
            rowHeight={53}
            stopEditingWhenCellsLoseFocus
            animateRows
            suppressCutToClipboard
            suppressMoveWhenRowDragging
            enableCellTextSelection
          />
        </Box>
      </div>
    </Stack>
  );
};

export default ChecklistBoardAgGridRefactored;
